import { motion, AnimatePresence, HTMLMotionProps } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { forwardRef } from "react";

interface LoadingButtonProps extends Omit<HTMLMotionProps<"button">, "ref"> {
  loading?: boolean;
  success?: boolean;
  locked?: boolean;
  loadingText?: string;
  successText?: string;
  lockedText?: string;
  variant?: "default" | "destructive" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}

const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  (
    {
      children,
      className,
      disabled,
      loading = false,
      success = false,
      locked = false,
      loadingText = "Saving...",
      successText = "Saved!",
      lockedText = "Locked",
      variant = "default",
      size = "default",
      ...props
    },
    ref
  ) => {
    const variants = {
      default:
        "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
      destructive:
        "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline:
        "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
      ghost: "hover:bg-accent hover:text-accent-foreground",
    };

    const sizes = {
      default: "h-10 px-4 py-2 min-w-[100px]",
      sm: "h-9 px-3 min-w-[90px]",
      lg: "h-11 px-8 min-w-[120px]",
      icon: "h-10 w-10",
    };

    return (
      <motion.button
        ref={ref}
        disabled={disabled || loading || locked}
        className={cn(
          "relative inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          variants[variant],
          sizes[size],
          locked &&
            variant === "default" &&
            "bg-red-500 hover:bg-red-600 text-white",
          locked && variant === "destructive" && "bg-red-700 hover:bg-red-800",
          className
        )}
        {...props}
      >
        <AnimatePresence mode='wait'>
          {locked ? (
            <motion.div
              key='locked'
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className='flex items-center justify-center w-full'
            >
              <X className='mr-2 h-4 w-4' />
              {lockedText}
            </motion.div>
          ) : loading ? (
            <motion.div
              key='loading'
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className='flex items-center justify-center w-full'
            >
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              {loadingText}
            </motion.div>
          ) : success ? (
            <motion.div
              key='success'
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={cn(
                "flex items-center justify-center w-full",
                variant === "default" && "text-primary-foreground",
                variant === "destructive" && "text-destructive-foreground"
              )}
            >
              <Check className='mr-2 h-4 w-4' />
              {successText}
            </motion.div>
          ) : (
            <motion.div
              key='default'
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              className='flex items-center justify-center w-full'
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }
);

LoadingButton.displayName = "LoadingButton";

export { LoadingButton };
